/* Pure geometry for the circular photo cropper (CropPhotoModal).

   The stage is a square of `size` CSS px, clipped to a circle. The photo is laid
   over it at a scale that covers the stage (like object-fit: cover) times the
   slider's zoom. `pan` is how far the photo's centre sits from the stage's
   centre, in CSS px — storing that rather than a top-left offset is what makes
   the zoom slider grow the photo about its middle instead of its corner.

   `iw`/`ih` and the source rect are in the photo's own pixels. */

export function coverScale(iw, ih, size) {
  return Math.max(size / iw, size / ih);
}

export function displaySize(iw, ih, size, zoom) {
  const scale = coverScale(iw, ih, size) * zoom;
  return { w: iw * scale, h: ih * scale, scale };
}

/* The stage must stay fully covered: the photo slides until one of its edges
   meets the stage edge, never past it. */
export function clampPan(pan, iw, ih, size, zoom) {
  const { w, h } = displaySize(iw, ih, size, zoom);
  const maxX = Math.max(0, (w - size) / 2);
  const maxY = Math.max(0, (h - size) / 2);
  return {
    x: Math.min(maxX, Math.max(-maxX, pan.x)),
    y: Math.min(maxY, Math.max(-maxY, pan.y)),
  };
}

/* Where the photo's top-left lands inside the stage, plus its drawn size. */
export function imageOffset(pan, iw, ih, size, zoom) {
  const { w, h } = displaySize(iw, ih, size, zoom);
  return { left: (size - w) / 2 + pan.x, top: (size - h) / 2 + pan.y, w, h };
}

/* The part of the original photo showing through the stage — what drawImage
   copies out. Always square, since the stage is. */
export function sourceRect(pan, iw, ih, size, zoom) {
  const { left, top } = imageOffset(pan, iw, ih, size, zoom);
  const { scale } = displaySize(iw, ih, size, zoom);
  return { sx: -left / scale, sy: -top / scale, sw: size / scale, sh: size / scale };
}
